import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // Add useLocation
import {
  Container, Card, Button, Form, Row, Col, Badge,
  Alert, Spinner, Tabs, Tab
} from 'react-bootstrap';
import { leadService } from '../../services/firebaseService';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import FollowUps from '../followups/FollowUps';

const LeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Add this
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [error, setError] = useState('');
  const { currentUser, isAdmin } = useAuth();

  // Check if this is an admin view from URL parameters
  const isAdminView = new URLSearchParams(location.search).get('admin') === 'true' || isAdmin();

  useEffect(() => {
    const fetchLead = async () => {
      try {
        setLoading(true);
        setError('');

        let leadData;
        if (isAdminView) {
          // Use admin service for admin view
          leadData = await adminService.getLeadById(leadId);
        } else {
          // Use regular lead service for agent view
          leadData = await leadService.getLeadById(leadId);
        }

        if (!leadData) {
          throw new Error('Lead not found');
        }

        setLead(leadData);
        setStatus(leadData.status);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching lead:', err);
      } finally {
        setLoading(false);
      }
    };

    if (leadId && currentUser) {
      fetchLead();
    }
  }, [leadId, currentUser, isAdminView]);

  const updateStatus = async () => {
    try {
      if (isAdminView) {
        await adminService.updateLeadStatus(leadId, status);
      } else {
        await leadService.updateLeadStatus(leadId, status);
      }
      setLead({ ...lead, status });
      setError('');
    } catch (err) {
      setError('Error updating status: ' + err.message);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'New': return 'primary';
      case 'Contacted': return 'info';
      case 'Visited': return 'secondary';
      case 'Qualified': return 'warning';
      case 'Closed': return 'success';
      case 'Lost': return 'danger';
      default: return 'secondary';
    }
  };

  const getBackPath = () => {
    if (isAdminView) {
      return '/admin/dashboard'; // Or '/admin/leads' depending on your setup
    }
    return isAdmin() ? '/admin/leads' : '/leads';
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    try {
      if (date.seconds) {
        return new Date(date.seconds * 1000).toLocaleString();
      }
      return new Date(date).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <Container className="text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading lead details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Lead</Alert.Heading>
          <p>{error}</p>
          <div className="d-flex gap-2 mt-3">
            <Button variant="outline-secondary" onClick={() => navigate(getBackPath())}>
              Back to {isAdminView ? 'Admin Dashboard' : (isAdmin() ? 'Leads Management' : 'My Leads')}
            </Button>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!lead) {
    return (
      <Container>
        <Alert variant="warning">
          <Alert.Heading>Lead Not Found</Alert.Heading>
          <p>The lead you're looking for doesn't exist or has been deleted.</p>
          <Button variant="outline-warning" onClick={() => navigate(getBackPath())}>
            Back to {isAdminView ? 'Admin Dashboard' : (isAdmin() ? 'Leads Management' : 'My Leads')}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <Button variant="outline-primary" onClick={() => navigate(getBackPath())}>
              ← Back to {isAdminView ? 'Admin Dashboard' : (isAdmin() ? 'Leads Management' : 'My Leads')}
            </Button>
            {isAdminView && (
              <Badge bg="info" className="fs-6">
                Admin View
              </Badge>
            )}
          </div>
        </Col>
      </Row>

      {isAdminView && (
        <Alert variant="info" className="mb-3">
          <strong>Admin View:</strong> You are viewing this lead as an administrator.
          {lead.assignedAgentEmail && (
            <span> Assigned Agent: <Badge bg="light" text="dark">{lead.assignedAgentEmail}</Badge></span>
          )}
        </Alert>
      )}

      {/* Rest of your LeadDetail component remains the same */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <h3 className="mb-1">{lead.fullName}</h3>
              <p className="text-muted mb-2">{lead.email} • {lead.mobileNo}</p>
              <div className="d-flex gap-2 align-items-center">
                <Badge bg={getStatusVariant(lead.status)} className="fs-6">
                  {lead.status}
                </Badge>
                <span className="text-muted">•</span>
                <span className="text-muted">
                  {lead.propertyType} • Rs.{lead.budget?.toLocaleString()}
                </span>
                <span className="text-muted">•</span>
                <span className="text-muted">{lead.location}</span>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <div className="d-flex flex-column gap-2">
                <Form.Group>
                  <Form.Label className="mb-1"><strong>Update Status:</strong></Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select 
                      value={status} 
                      onChange={(e) => setStatus(e.target.value)}
                      size="sm"
                    >
                      <option value="New">New</option>
                      <option value="Contacted">Contacted</option>
                      <option value="Visited">Visited</option>
                      <option value="Qualified">Qualified</option>
                      <option value="Closed">Closed</option>
                      <option value="Lost">Lost</option>
                    </Form.Select>
                    <Button 
                      variant="primary" 
                      onClick={updateStatus} 
                      size="sm"
                      disabled={status === lead.status}
                    >
                      Update
                    </Button>
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs
        activeKey={activeTab}
        onSelect={(tab) => setActiveTab(tab)}
        className="mb-3"
        fill
      >
        <Tab eventKey="details" title="Lead Details">
          {/* Your existing lead details content */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Lead Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Personal Information</strong>
                    <hr className="my-2" />
                    <p><strong>Full Name:</strong> {lead.fullName}</p>
                    <p><strong>Email:</strong> {lead.email}</p>
                    <p><strong>Phone:</strong> {lead.mobileNo}</p>
                    {isAdminView && (
                      <p><strong>Assigned Agent:</strong> {lead.assignedAgentEmail || 'Not assigned'}</p>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Property Requirements</strong>
                    <hr className="my-2" />
                    <p><strong>Property Type:</strong> {lead.propertyType}</p>
                    <p><strong>Budget:</strong> Rs.{lead.budget?.toLocaleString()}</p>
                    <p><strong>Location:</strong> {lead.location}</p>
                    <p><strong>Source:</strong> {lead.source}</p>
                  </div>
                </Col>
              </Row>
              {/* Add more lead details as needed */}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="followups" title={
          <span>
            Follow-ups
            {lead.followUps && lead.followUps.length > 0 && (
              <Badge bg="primary" className="ms-2">
                {lead.followUps.length}
              </Badge>
            )}
          </span>
        }>
          <FollowUps leadId={leadId} />
        </Tab>
      </Tabs>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
    </Container>
  );
};

export default LeadDetail;